import { datadogRum } from '@datadog/browser-rum-slim';
import { addToCartEvent } from '@portal/analytics/events/buildBundle';
import { selectProductEvent } from '@portal/analytics/events/studio';
import { createShopifyCart } from '@portal/api/shopifyV2/cart';
import { CartDetails } from '@portal/api/shopifyV2/types';
import { SHOPIFY_STORE_BRAND } from '@portal/config/base';
import { MONITORING_TAGS } from '@portal/constants/monitoring';
import { useSiteCart } from '@portal/shared/components/SiteCart/hooks/SiteCartContext';
import { useLocale } from '@portal/shared/hooks/useLocale';
import { isMobileTablet } from '@portal/shared/utils/devices';
import { safeLocalStorage } from '@portal/shared/utils/safeLocalStorage';
import { getIdFromShopifyFormattedId } from '@portal/shared/utils/shopify/ids';
import { isCoreProduct } from '@portal/shared/utils/shopify/isCoreProduct';
import { isOTPpacksVariantV53 } from '@portal/shared/utils/shopify/isOTPpacksVariantV53';
import { isOTPpouchVariant } from '@portal/shared/utils/shopify/isOTPpouchVariant';
import { isStandaloneProduct } from '@portal/shared/utils/shopify/isStandaloneProduct';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useStoreCurrency } from '@portal/state/hooks/useStoreCurrency';
import { getTrackingParams } from '@portal/state/localStorage/trackingParams';
import { useProductStore } from '@portal/state/store/pdpStore';
import { FormattedShopifyCollection } from '@portal/types/shopify/product.formatted.types';
import { useGateValue } from '@statsig/react-bindings';
import { addShopifyCartLines } from 'api/shopifyV2/cart/cartLinesAdd';
import { useSessionValue } from 'appLayer/auth';
import { useRouter } from 'next/router';
import { constructBundleCartPayload } from 'processes/ShopListBundles/utils';
import { useCallback, useMemo } from 'react';
import { useShopifyCartUrl } from 'shared/hooks/useShopifyCartUrl';
import { SSGates } from 'state/context/StatsigContext/StatsigFlags';

export const useBundlePdpCheckout = () => {
  const store = useStoreValue();
  const router = useRouter();
  const pageSlug = router.asPath;
  const locale = useLocale();
  const session = useSessionValue();
  const { currency } = useStoreCurrency();
  const isMobileTabletDevice = isMobileTablet();
  const trackingParams = getTrackingParams();
  const { getShopifyCartUrl } = useShopifyCartUrl();
  const { flavor } = useProductStore();

  // New cart
  const { cart, cartId, openCart, refetchCartData } = useSiteCart();

  const enableOpenCart = useGateValue(SSGates.enableShopAllAddToCart);

  const currentCartId = useMemo(() => cartId, [cartId]);
  const cartExists = Boolean(cart?.lines?.length && cart.lines.length > 0);

  const utmString = Object.keys(trackingParams)
    .filter((key) => key.startsWith('utm_'))
    .map((key) => `${key}=${trackingParams[key]}`)
    .join('&');

  const fireAddToCartEvent = useCallback(
    (currentCart: CartDetails | null) => {
      if (!currentCart?.lines) return;

      const { id, lines, cost } = currentCart;

      const firstPrimaryItem =
        lines.find(
          (line) =>
            !!line.sellingPlanAllocation?.sellingPlan.id ||
            isOTPpouchVariant(line.merchandise.id, store) ||
            isOTPpacksVariantV53(line.merchandise.id, store)
        ) || lines[0];

      const mappedLines = lines.map((line: any) => {
        const itemId = Number(getIdFromShopifyFormattedId(line.merchandise.id));

        return {
          item_id: itemId,
          item_name: line.merchandise.product.title,
          item_brand: SHOPIFY_STORE_BRAND[store],
          item_variant: line.merchandise.title,
          variant_id: itemId,
          price: Number(line.merchandise.priceV2.amount),
          currency,
          quantity: line.quantity
        };
      });

      addToCartEvent({
        item_list_name: firstPrimaryItem.merchandise.product.title,
        item_list_id: firstPrimaryItem.merchandise.id,
        affiliation: utmString || pageSlug,
        items: mappedLines,
        eventId: id,
        store,
        currency,
        value: cost.subtotalAmount.amount,
        deviceType: isMobileTabletDevice ? 'mobile' : 'desktop',
        isStandaloneProduct:
          lines.length === 1 && isStandaloneProduct(lines[0].merchandise.id, store)
      });
    },
    [currency, isMobileTabletDevice, pageSlug, store, utmString]
  );

  const handleCreateBundlePdpCheckout = useCallback(
    async (collectionVariants: FormattedShopifyCollection) => {
      const filteredProducts = collectionVariants.products.filter((variant) => {
        if (!flavor) return true;

        const flavorMetafield = variant.metafields?.find(
          (metafield) => metafield?.key === 'flavor'
        );

        return isCoreProduct(variant) && flavorMetafield
          ? flavorMetafield.value.toLowerCase().includes(flavor)
          : true;
      });

      const filteredCollection = { ...collectionVariants, products: filteredProducts };

      const cartPayload = constructBundleCartPayload(
        store,
        filteredCollection,
        utmString,
        session?.customer?.tags
      );

      if (!cartPayload) return;

      try {
        let nextCurrentCartId;

        if (enableOpenCart && cartExists && currentCartId) {
          const { id: updatedCartId } = await addShopifyCartLines(store, {
            ...cartPayload,
            cartId: currentCartId
          });
          nextCurrentCartId = updatedCartId;
        } else {
          const { id: newCartId } = await createShopifyCart(store, cartPayload);
          nextCurrentCartId = newCartId;
        }

        // Use first core product for event
        const mainVariant = filteredCollection.products.find((product) => isCoreProduct(product));
        if (mainVariant) {
          selectProductEvent({
            store,
            itemId: mainVariant.id,
            itemName: mainVariant.title,
            location: pageSlug,
            isStandaloneProduct: isStandaloneProduct(mainVariant.id, store)
          });
        }

        if (nextCurrentCartId) {
          if (enableOpenCart) {
            const cartData = await refetchCartData(nextCurrentCartId);
            openCart();

            safeLocalStorage.setItem('cartId', nextCurrentCartId);
            fireAddToCartEvent(cartData);
          } else {
            const cartUrl = await getShopifyCartUrl(store, nextCurrentCartId, locale);
            await router.push(cartUrl);
          }
        }
      } catch (error) {
        const tags = {
          [MONITORING_TAGS.MARKETING]: true,
          [MONITORING_TAGS.CART]: true
        };
        datadogRum.addError(error, { tags });
      }
    },
    [
      store,
      utmString,
      enableOpenCart,
      cartExists,
      currentCartId,
      pageSlug,
      flavor,
      refetchCartData,
      openCart,
      fireAddToCartEvent,
      getShopifyCartUrl,
      locale,
      router,
      session?.customer?.tags
    ]
  );

  return { handleCreateBundlePdpCheckout };
};
